import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { CloseOutlined, ArrowBack } from "@mui/icons-material";
import { modalCloseButtonClassName } from "@config/theme/dialog";
import {
	MODAL_HEADER_LAYOUT_CLASS,
	MODAL_HEADER_SURFACE_CLASS,
	MODAL_TITLE_CLASS,
} from "@config/theme/modal";
import type { GenericModalProps } from "./types";
import InfoTooltip from "../InfoTooltip/InfoTooltip";
import ModalCloseButton from "./ModalCloseButton";

const Modal: React.FC<GenericModalProps> = ({
	isOpen,
	onClose,
	title,
	icon,
	showCloseIcon = true,
	children,
	footer,
	width = "max-w-md",
	height = "auto",
	backgroundColor = "bg-white",
	rounded = "rounded-2xl",
	padding = "p-6",
	showBackIcon,
	onBack,
	className = "",
	isTooltipEnabled,
	tooltipText,
	brandedHeader = false,
}) => {
	// Prevent ESC key from closing modal if showCloseIcon is false
	useEffect(() => {
		if (!isOpen) return;

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !showCloseIcon) {
				e.preventDefault();
				e.stopPropagation();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isOpen, showCloseIcon]);

	if (!isOpen) return null;

	const hasHeaderContent = Boolean(title || icon || showBackIcon || isTooltipEnabled);
	const closeButtonClassName = `${modalCloseButtonClassName} absolute top-6 right-6 z-10`;

	const overlayAlignClass = brandedHeader
		? "items-start justify-center p-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-4 sm:items-center sm:p-4"
		: "items-center justify-center";

	return createPortal(
		<div
			className={`fixed inset-0 z-[9999] flex ${overlayAlignClass} bg-black bg-opacity-30`}
			role="dialog"
			onClick={e => {
				if (showCloseIcon && e.target === e.currentTarget) {
					onClose();
				}
			}}>
			<div
				className={`my-auto flex w-full min-h-0 flex-col overflow-hidden ${width} ${height} ${backgroundColor} ${rounded} ${brandedHeader ? "" : padding} relative shadow-xl ${className}`}
				onClick={e => e.stopPropagation()}>
				{brandedHeader && hasHeaderContent && (
					<div className={[MODAL_HEADER_LAYOUT_CLASS, MODAL_HEADER_SURFACE_CLASS].join(" ")}>
						<div className="flex min-w-0 flex-1 items-center gap-2">
							{showBackIcon && (
								<button
									type="button"
									onClick={onBack}
									className="rounded-full p-1 text-white transition-colors hover:bg-white/15"
								>
									<ArrowBack className="h-5 w-5" />
								</button>
							)}
							{icon && <div className="shrink-0 text-white">{icon}</div>}
							{title &&
								(typeof title === "string" ? (
									<h2 className={`min-w-0 ${MODAL_TITLE_CLASS}`}>{title}</h2>
								) : (
									<div className={`min-w-0 ${MODAL_TITLE_CLASS}`}>{title}</div>
								))}
							{isTooltipEnabled && <InfoTooltip tooltip={tooltipText} />}
						</div>
						{showCloseIcon && <ModalCloseButton onClose={onClose} />}
					</div>
				)}

				{!brandedHeader && showCloseIcon && (
					<button type="button" onClick={onClose} className={closeButtonClassName} aria-label="Close">
						<CloseOutlined sx={{ fontSize: 18 }} />
					</button>
				)}

				{!brandedHeader && hasHeaderContent && (
					<div
						className={`flex items-start mb-4 flex-shrink-0 ${showCloseIcon ? "pr-12" : ""} ${padding}`}
					>
						<div className="flex items-center gap-2 min-w-0">
							{showBackIcon && (
								<button
									onClick={onBack}
									className="text-gray-600 hover:text-gray-900 transition-colors p-1 -ml-1 rounded-full bg-white"
								>
									<ArrowBack className="w-5 h-5" />
								</button>
							)}
							{icon && <div className="text-xl text-primary">{icon}</div>}
							{title && (
								<h2 className="text-lg font-semibold text-gray-900">
									{typeof title === "string" ? title : title}
								</h2>
							)}
							{isTooltipEnabled && <InfoTooltip tooltip={tooltipText} />}
						</div>
					</div>
				)}

				<div
					className={
						height === "max-h-[90vh]" || className.includes("flex flex-col")
							? `flex-1 min-h-0 flex flex-col ${brandedHeader ? "" : padding === "p-0" ? "" : ""}`
							: brandedHeader
								? ""
								: padding
					}
				>
					{children}
				</div>

				{footer && (
					<div className={`flex-shrink-0 ${brandedHeader ? "" : "mt-6"}`}>{footer}</div>
				)}
			</div>
		</div>,
		document.body,
	);
};

export default Modal;
